import type { InteriorPalette, RoomSetMaterials } from "building-types/materials/interiorPaletteTypes";
import { PaletteType } from "building-types/materials/paletteType";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { InteriorMaterialSettings } from "building-types/settings/interiorMaterialSettings";
import { SettingsType } from "building-types/settings/settingsType";
import {
  MS_Carpet__L__Carpet_Texture__Carpet_Texture_Oatmeal,
  MS_Color__C,
  MS_Floor_Concrete__Default,
  MS_Floor_Tile_Carrara__Default,
  MS_Glass_Base__Default,
  MS_Metal_Powder__C,
  MS_Paint_Egg__C,
  MS_Paint_Satin__C,
  MS_Paint_SemiGloss__C,
  MS_Wood_StaggeredPlank__L__Wood_StaggeredPlank__StaggeredPlank_Tower,
} from "common/materials/library/designMaterialSrcDefaults";
import { DMC } from "common/materials/library/DMC";
import uid from "common/utils/uid";
import { BuiltinHomeSettingsId } from "./builtinHomeSettingsId";

export enum InteriorPaletteName {
  DEFAULT = "DEFAULT",
  DEBUG = "DEBUG",
  INTERIOR_B01 = "INTERIOR_B01",
  INTERIOR_B02 = "INTERIOR_B02",
}

const interiorPalettePrettyName: { [key in InteriorPaletteName]: string } = {
  [InteriorPaletteName.DEFAULT]: "Default Interior Palette",
  [InteriorPaletteName.DEBUG]: "Debug Interior Palette",
  [InteriorPaletteName.INTERIOR_B01]: "Morning Mist",
  [InteriorPaletteName.INTERIOR_B02]: "Spring Fling",
};

const defaultRoomPalettes: RoomSetMaterials[] = [
  {
    roomTypes: [RoomType.CHASE],
    wallInterior0: { ...MS_Color__C, colorId: DMC.SystemColors.Black },
    wallInterior1: { ...MS_Color__C, colorId: DMC.SystemColors.Black },
    wallInterior2: { ...MS_Color__C, colorId: DMC.SystemColors.Black },
  },
];

export const defaultInteriorPalette = (params: Partial<InteriorPalette> = {}): InteriorPalette => ({
  id: uid(),
  entityType: "PALETTE",
  sortIndex: 0,
  type: PaletteType.INTERIOR_PALETTE,
  name: interiorPalettePrettyName[InteriorPaletteName.DEFAULT],
  defaultRoomMaterials: {
    floorInterior0: { ...MS_Color__C, colorId: DMC.SystemColors.Gray55 },
    wallInterior0: { ...MS_Color__C, colorId: DMC.SystemColors.Gray70 },
    ceiling0: { ...MS_Paint_Egg__C, colorId: DMC.PaintSpare_Colors.White },
    glass0: MS_Glass_Base__Default,
    trimInterior0: { ...MS_Color__C, colorId: DMC.SystemColors.Gray90 },
    doorInterior0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Green },
    doorFixture0: { ...MS_Color__C, colorId: DMC.SystemColors.Gray55 },
    propDefault0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Blue },
  },
  roomMaterials: [...defaultRoomPalettes],
  ...params,
});

const interiorPaletteLibrary: { [paletteId: string]: InteriorPalette } = {
  [InteriorPaletteName.INTERIOR_B01]: {
    ...defaultInteriorPalette(),
    id: InteriorPaletteName.INTERIOR_B01,
    name: interiorPalettePrettyName[InteriorPaletteName.INTERIOR_B01],
    defaultRoomMaterials: {
      ...defaultInteriorPalette().defaultRoomMaterials,
      floorInterior0: { ...MS_Color__C, colorId: DMC.SystemColors.Orange },
      wallInterior0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Orange },
      ceiling0: { ...MS_Paint_Satin__C, colorId: DMC.SherwinWilliams.SW_7005_PureWhite },
      trimInterior0: { ...MS_Paint_SemiGloss__C, colorId: DMC.PaintSpare_Colors.White },
      doorInterior0: { ...MS_Paint_SemiGloss__C, colorId: DMC.SherwinWilliams.SW_7005_PureWhite },
      doorFixture0: { ...MS_Metal_Powder__C, colorId: DMC.MetalPowder_Colors.Slate },
      propDefault0: { ...MS_Color__C, colorId: DMC.SystemColors.White },
    },
    roomMaterials: [
      ...defaultRoomPalettes,

      //FLOORING
      {
        roomTypes: [
          RoomType.CLOSET,
          RoomType.DINING,
          RoomType.DINING_NOOK,
          RoomType.ENTRY,
          RoomType.FORMAL_DINING,
          RoomType.FORMAL_LIVING,
          RoomType.FOYER,
          RoomType.GENERAL,
          RoomType.GENERAL_OPEN,
          RoomType.GALLERY,
          RoomType.SCREEN,
          RoomType.LIVING_HALL,
          RoomType.HALL,
          RoomType.COAT_CLOSET,
          RoomType.HALL_NO_ENTRY,
          RoomType.KITCHEN_HALL,
          RoomType.KITCHEN,
          RoomType.KITCHEN_CABINETS,
          RoomType.LIVING,
          RoomType.OFFICE,
          RoomType.LIBRARY,
          RoomType.PANTRY,
          RoomType.READING_NOOK,
          RoomType.STAIRS,
          RoomType.STAIRS_EDITOR,
          RoomType.SOLARIUM,
          RoomType.BAY_WINDOW,
          RoomType.FLEX,
          RoomType.BATH_HALL,
          RoomType.CORNER_HALL,
          RoomType.UTIL_HALL,
          RoomType.MASTER_VESTIBULE,
          RoomType.HALL_CASED_OPENING,
          RoomType.REFRIGERATOR,
          RoomType.MUDROOM,
          RoomType.JUMP,
        ],
        floorInterior0: MS_Wood_StaggeredPlank__L__Wood_StaggeredPlank__StaggeredPlank_Tower,
      },
      {
        roomTypes: [RoomType.GARAGE, RoomType.MECH, RoomType.WORKSHOP, RoomType.NONE, RoomType.CHASE],
        floorInterior0: MS_Floor_Concrete__Default,
      },
      {
        roomTypes: [
          RoomType.MASTER_BED,
          RoomType.MASTER_CLOSET,
          RoomType.MASTER_HALL,
          RoomType.MASTER_PASS,
          RoomType.MASTER_SEAT,
          RoomType.BEDROOM,
          RoomType.BED_PASS,
          RoomType.BED_CLOSET,
          RoomType.REACH_CLOSET,
          RoomType.GUEST_SUITE,
          RoomType.WALK_IN_CLOSET,
        ],
        floorInterior0: MS_Carpet__L__Carpet_Texture__Carpet_Texture_Oatmeal,
      },
      {
        roomTypes: [
          RoomType.BATHFULL,
          RoomType.BATHHALF,
          RoomType.MASTER_BATH,
          RoomType.WATER_CLOSET,
          RoomType.LAUNDRY,
          RoomType.DOG,
          RoomType.POWDER,
          RoomType.SHOWER,
          RoomType.BATHTUB,
          RoomType.MASTER_VANITY,
          RoomType.BATH_HALL,
          RoomType.BED_BATH,
        ],
        floorInterior0: MS_Floor_Tile_Carrara__Default,
      },

      //WALLS
      {
        roomTypes: [
          RoomType.BATHHALF,
          RoomType.BED_CLOSET,
          RoomType.CLOSET,
          RoomType.DINING,
          RoomType.ENTRY,
          RoomType.FORMAL_DINING,
          RoomType.FORMAL_LIVING,
          RoomType.FOYER,
          RoomType.JUMP,
          RoomType.DOG,
          RoomType.GENERAL,
          RoomType.GENERAL_OPEN,
          RoomType.GALLERY,
          RoomType.SCREEN,
          RoomType.LIVING_HALL,
          RoomType.HALL,
          RoomType.COAT_CLOSET,
          RoomType.UTIL_HALL,
          RoomType.HALL_NO_ENTRY,
          RoomType.KITCHEN,
          RoomType.KITCHEN_HALL,
          RoomType.KITCHEN_CABINETS,
          RoomType.REFRIGERATOR,
          RoomType.LAUNDRY,
          RoomType.LIVING,
          RoomType.MUDROOM,
          RoomType.OPEN_BELOW,
          RoomType.PANTRY,
          RoomType.POWDER,
          RoomType.REACH_CLOSET,
          RoomType.STAIRS,
          RoomType.STAIRS_EDITOR,
          RoomType.WALK_IN_CLOSET,
          RoomType.SOLARIUM,
          RoomType.BAY_WINDOW,
          RoomType.BATH_HALL,
          RoomType.CORNER_HALL,
          RoomType.MASTER_VESTIBULE,
          RoomType.HALL_CASED_OPENING,
        ],
        wallInterior0: { ...MS_Paint_Satin__C, colorId: DMC.SherwinWilliams.SW_6385_DoverWhite },
      },
      {
        roomTypes: [
          RoomType.MASTER_BED,
          RoomType.MASTER_PASS,
          RoomType.MASTER_HALL,
          RoomType.MASTER_SEAT,
          RoomType.MASTER_CLOSET,
          RoomType.READING_NOOK,
          RoomType.GARAGE,
          RoomType.WORKSHOP,
          RoomType.MECH,
          RoomType.NONE,
        ],
        wallInterior0: { ...MS_Paint_Satin__C, colorId: DMC.PaintSpare_Colors.TH1 },
      },
      {
        roomTypes: [
          RoomType.MASTER_BATH,
          RoomType.WATER_CLOSET,
          RoomType.BED_BATH,
          RoomType.SHOWER,
          RoomType.BATHTUB,
          RoomType.MASTER_VANITY,
          RoomType.BATH_HALL,
        ],
        wallInterior0: { ...MS_Paint_SemiGloss__C, colorId: DMC.PaintSpare_Colors.TH2 },
      },
      {
        roomTypes: [RoomType.BEDROOM, RoomType.BED_PASS, RoomType.GUEST_SUITE, RoomType.DINING_NOOK],
        wallInterior0: { ...MS_Paint_Satin__C, colorId: DMC.PaintSpare_Colors.TH3 },
      },
      {
        roomTypes: [RoomType.BATHFULL],
        wallInterior0: { ...MS_Paint_SemiGloss__C, colorId: DMC.PaintSpare_Colors.TH4 },
      },
      {
        roomTypes: [RoomType.OFFICE, RoomType.LIBRARY, RoomType.FLEX],
        wallInterior0: { ...MS_Paint_Satin__C, colorId: DMC.PaintSpare_Colors.TH5 },
      },
      {
        roomTypes: [RoomType.FIREPLACE, RoomType.FREESTANDING_FIREPLACE],
        wallInterior0: { ...MS_Paint_Egg__C, colorId: DMC.SherwinWilliams.SW_6258_TricornBlack },
        floorInterior0: { ...MS_Paint_Egg__C, colorId: DMC.SherwinWilliams.SW_6258_TricornBlack },
      },
    ],
  },
  [InteriorPaletteName.INTERIOR_B02]: {
    ...defaultInteriorPalette(),
    id: InteriorPaletteName.INTERIOR_B02,
    name: interiorPalettePrettyName[InteriorPaletteName.INTERIOR_B02],
    defaultRoomMaterials: {
      ...defaultInteriorPalette().defaultRoomMaterials,
      floorInterior0: { ...MS_Color__C, colorId: DMC.SystemColors.Orange },
      wallInterior0: { ...MS_Color__C, colorId: DMC.SystemColors.Light_Orange },
      ceiling0: { ...MS_Paint_Satin__C, colorId: DMC.SherwinWilliams.SW_7005_PureWhite },
      trimInterior0: { ...MS_Paint_SemiGloss__C, colorId: DMC.PaintSpare_Colors.Cream },
      doorInterior0: { ...MS_Paint_SemiGloss__C, colorId: DMC.SherwinWilliams.SW_7005_PureWhite },
      doorFixture0: { ...MS_Metal_Powder__C, colorId: DMC.MetalPowder_Colors.Slate },
      propDefault0: { ...MS_Color__C, colorId: DMC.SystemColors.White },
    },

    roomMaterials: [
      //FLOORING
      {
        roomTypes: [
          RoomType.CLOSET,
          RoomType.DINING,
          RoomType.DINING_NOOK,
          RoomType.ENTRY,
          RoomType.FORMAL_DINING,
          RoomType.FORMAL_LIVING,
          RoomType.FOYER,
          RoomType.GENERAL,
          RoomType.GENERAL_OPEN,
          RoomType.GALLERY,
          RoomType.SCREEN,
          RoomType.LIVING_HALL,
          RoomType.HALL,
          RoomType.COAT_CLOSET,
          RoomType.UTIL_HALL,
          RoomType.HALL_NO_ENTRY,
          RoomType.KITCHEN,
          RoomType.KITCHEN_HALL,
          RoomType.KITCHEN_CABINETS,
          RoomType.REFRIGERATOR,
          RoomType.LAUNDRY,
          RoomType.LIVING,
          RoomType.OFFICE,
          RoomType.LIBRARY,
          RoomType.PANTRY,
          RoomType.READING_NOOK,
          RoomType.STAIRS,
          RoomType.STAIRS_EDITOR,
          RoomType.SOLARIUM,
          RoomType.BAY_WINDOW,
          RoomType.FLEX,
          RoomType.BATH_HALL,
          RoomType.CORNER_HALL,
          RoomType.MASTER_VESTIBULE,
          RoomType.HALL_CASED_OPENING,
          RoomType.MASTER_BED,
          RoomType.MASTER_CLOSET,
          RoomType.MASTER_HALL,
          RoomType.MASTER_PASS,
          RoomType.MASTER_SEAT,
          RoomType.BEDROOM,
          RoomType.BED_PASS,
          RoomType.BED_CLOSET,
          RoomType.REACH_CLOSET,
          RoomType.GUEST_SUITE,
          RoomType.WALK_IN_CLOSET,
          RoomType.BATHFULL,
          RoomType.BATHHALF,
          RoomType.BED_BATH,
          RoomType.MASTER_BATH,
          RoomType.WATER_CLOSET,
          RoomType.MUDROOM,
          RoomType.JUMP,
          RoomType.DOG,
          RoomType.POWDER,
          RoomType.SHOWER,
          RoomType.BATHTUB,
          RoomType.MASTER_VANITY,
          RoomType.BATH_HALL,
        ],
        floorInterior0: MS_Wood_StaggeredPlank__L__Wood_StaggeredPlank__StaggeredPlank_Tower,
      },
      {
        roomTypes: [RoomType.GARAGE, RoomType.MECH, RoomType.WORKSHOP, RoomType.NONE, RoomType.CHASE],
        floorInterior0: MS_Floor_Concrete__Default,
      },

      //WALLS
      {
        roomTypes: [
          RoomType.BATHHALF,
          RoomType.BED_BATH,
          RoomType.BED_CLOSET,
          RoomType.CLOSET,
          RoomType.DINING,
          RoomType.ENTRY,
          RoomType.FORMAL_DINING,
          RoomType.FORMAL_LIVING,
          RoomType.FOYER,
          RoomType.JUMP,
          RoomType.DOG,
          RoomType.GALLERY,
          RoomType.SCREEN,
          RoomType.LIVING_HALL,
          RoomType.HALL,
          RoomType.COAT_CLOSET,
          RoomType.UTIL_HALL,
          RoomType.HALL_NO_ENTRY,
          RoomType.KITCHEN,
          RoomType.KITCHEN_HALL,
          RoomType.KITCHEN_CABINETS,
          RoomType.REFRIGERATOR,
          RoomType.LAUNDRY,
          RoomType.LIVING,
          RoomType.MUDROOM,
          RoomType.OPEN_BELOW,
          RoomType.PANTRY,
          RoomType.POWDER,
          RoomType.REACH_CLOSET,
          RoomType.STAIRS,
          RoomType.STAIRS_EDITOR,
          RoomType.WALK_IN_CLOSET,
          RoomType.SOLARIUM,
          RoomType.BAY_WINDOW,
          RoomType.BATH_HALL,
          RoomType.CORNER_HALL,
          RoomType.MASTER_VESTIBULE,
          RoomType.HALL_CASED_OPENING,
        ],
        wallInterior0: { ...MS_Paint_Satin__C, colorId: DMC.SherwinWilliams.SW_6385_DoverWhite },
      },
      {
        roomTypes: [
          RoomType.MASTER_BED,
          RoomType.MASTER_PASS,
          RoomType.MASTER_HALL,
          RoomType.MASTER_SEAT,
          RoomType.MASTER_CLOSET,
          RoomType.READING_NOOK,
          RoomType.GARAGE,
          RoomType.GENERAL,
          RoomType.GENERAL_OPEN,
          RoomType.WORKSHOP,
          RoomType.MECH,
          RoomType.NONE,
          RoomType.CHASE,
        ],
        wallInterior0: { ...MS_Paint_Satin__C, colorId: DMC.SherwinWilliams.SW_9141_Waterloo },
      },
      {
        roomTypes: [
          RoomType.MASTER_BATH,
          RoomType.WATER_CLOSET,
          RoomType.BED_BATH,
          RoomType.SHOWER,
          RoomType.BATHTUB,
          RoomType.MASTER_VANITY,
          RoomType.BATH_HALL,
        ],
        wallInterior0: { ...MS_Paint_SemiGloss__C, colorId: DMC.SherwinWilliams.SW_6224_MountainAir },
      },
      {
        roomTypes: [
          RoomType.BEDROOM,
          RoomType.BED_PASS,
          RoomType.GUEST_SUITE,
          RoomType.OFFICE,
          RoomType.LIBRARY,
          RoomType.FLEX,
        ],
        wallInterior0: { ...MS_Paint_Satin__C, colorId: DMC.SherwinWilliams.SW_9147_FavoriteJeans },
      },
      {
        roomTypes: [RoomType.FIREPLACE, RoomType.FREESTANDING_FIREPLACE],
        wallInterior0: { ...MS_Paint_Egg__C, colorId: DMC.SherwinWilliams.SW_6258_TricornBlack },
        floorInterior0: { ...MS_Paint_Egg__C, colorId: DMC.SherwinWilliams.SW_6258_TricornBlack },
      },
      {
        roomTypes: [RoomType.BATHFULL],
        wallInterior0: {
          ...MS_Paint_SemiGloss__C,
          colorId: DMC.SherwinWilliams.SW_6900_OptimisticYellow,
        },
      },
      {
        roomTypes: [RoomType.DINING_NOOK],
        wallInterior0: { ...MS_Paint_Satin__C, colorId: DMC.SherwinWilliams.SW_9110_Malabar },
      },
    ],
  },
};

export const defaultInteriorMaterialSettings: InteriorMaterialSettings = {
  type: SettingsType.INTERIOR_MATERIAL_SETTINGS,
  name: BuiltinHomeSettingsId.Default,
  id: BuiltinHomeSettingsId.Default + "_interior",

  palettes: [InteriorPaletteName.INTERIOR_B01],
  paletteSlotsUserDefinedNames: {
    [InteriorPaletteName.INTERIOR_B01]: {},
  },
};

export default interiorPaletteLibrary;
